import { faCrown } from '@fortawesome/pro-light-svg-icons/faCrown'
import Icon from '@space-core/components/icon'

import useTranslation from '@space-core/hooks/useTranslation'
import type { Product, SectionProductDetail } from '@space-shell/common/services/sections'
import {
  getCouponStudent,
  getProductActivePrice
} from '@space-shell/modules/products/utils/productCoupon'
import {
  getSubscriptionCoupon,
  isSubscriptionPlan
} from '@space-shell/modules/products/utils/subscriptionProduct'
import clsx from 'clsx'

import styled from 'styled-components'

const YellowTag = styled.p`
  color: var(--yellow);
`

const FullPriceText = styled.span`
  text-decoration: line-through;
`

type ProductPriceProps = {
  checkoutPrice: {
    fullPrice: string | null
    price?: string | null
  } | null
  product: Product | SectionProductDetail
  isSectionProduct?: boolean
}

export const ProductPrice = ({
  checkoutPrice,
  product,
  isSectionProduct = false
}: ProductPriceProps) => {
  const isSubscriptionPlanActive = isSubscriptionPlan(product)
  const productPrice = getProductActivePrice(isSubscriptionPlanActive, checkoutPrice)
  const price = product?.price || productPrice
  const hasStudentCoupon = getCouponStudent(product)
  const { t } = useTranslation()

  if (
    (!checkoutPrice?.fullPrice && !checkoutPrice?.price) ||
    (!isSubscriptionPlanActive && !product?.couponInfo)
  ) {
    return (
      <p
        className={clsx('_text-1 _font-weight-bold _text-green-light _mb-0', {
          '_text-2 _mt-2': isSectionProduct
        })}
        data-test='price'>
        {price}
      </p>
    )
  }

  if (isSubscriptionPlanActive && price) {
    const coupon = getSubscriptionCoupon(product)

    return (
      <p
        className={clsx('_text-gray-900', {
          '_text-2': isSectionProduct,
          '_text-1': !isSectionProduct
        })}
        data-test='price'>
        {t('sales.subscription.price')}
        <span className='_text-green-light _font-weight-bold _ml-1'>
          {price}/{t('sales.subscription.periodicity.month')}
        </span>
        {coupon > 0 && !isSectionProduct && <span className='_ml-1'>{`(-${coupon}%)`}</span>}
      </p>
    )
  }

  return (
    <div
      className={clsx('_d-flex', {
        '_flex-column _align-items-start': isSectionProduct,
        '_align-items-center': !isSectionProduct
      })}>
      <p className={clsx('_text-1 _text-gray-500 _mb-0 ', { '_text-2 _mt-2': isSectionProduct })}>
        <span className='_text-green-light _font-weight-bold _mr-1'>{price}</span>
        <FullPriceText>{checkoutPrice?.fullPrice}</FullPriceText>
      </p>
      {hasStudentCoupon && (
        <div className='_d-flex _align-items-center'>
          {!isSectionProduct ? <div className='_mx-1 _text-gray-600 _mb-2'>.</div> : null}
          <YellowTag className={clsx('_text-1 _m-0', { '_text-2 _mt-1': isSectionProduct })}>
            <Icon icon={faCrown} className='_mr-1' />
            {t('tags.student_only_discount')}
          </YellowTag>
        </div>
      )}
    </div>
  )
}
